// lib
import { useReducer, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'

import { sidebarReducer, DEFAULT_STATE } from '@aytm/app/stats/page/sidebar/components/reducers'
import { DataContext } from '@aytm/app/stats/page/sidebar/components/context'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

// components
import SidebarSwitches from '@aytm/app/stats/page/sidebar/SidebarSwitches'
import SidebarContent from '@aytm/app/stats/page/sidebar/SidebarContent'

// styles
import style from '@aytm/app/stats/page/sidebar/components/styles/Sidebar.module.scss'

const Sidebar = ({
  container,
  sidebar,
  survey,
  model,
  parser,
  features,
  currentPanel,
  onLoad,
  onPanelChange,
  ...props
}) => {
  const [state, dispatch] = useReducer(sidebarReducer, DEFAULT_STATE)

  const switchesRef = useRef(null)
  const contentRef = useRef(null)

  const throttleTimerRef = useRef(null)
  const resizeObserverRef = useRef(null)
  const intersectionObserverRef = useRef(null)

  const dataContextValue = useMemo(
    () => ({
      sidebarState: state,
      sidebarDispatch: dispatch
    }),
    [state, dispatch]
  )

  const updateSidebarHeight = () => {
    if (null === switchesRef.current || null === contentRef.current) return

    const sScrollTop = switchesRef.current.scrollTop
    const cScrollTop = contentRef.current.scrollTop

    switchesRef.current.style.height = 0
    contentRef.current.style.height = 0

    const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

    const switchesRect = switchesRef.current.getBoundingClientRect()
    const contentsRect = contentRef.current.getBoundingClientRect()

    // The bottom edge the sidebar columns may extend to: the viewport bottom, pulled up by the
    // footer when one is present (clamped to the footer's top once the footer scrolls into view).
    let bottom = h

    const footer = document.querySelector('.-footer')
    if (footer != undefined) {
      const footerRect = footer.getBoundingClientRect()
      bottom = footerRect.y + footerRect.height <= h ? h - footerRect.height : Math.min(h, footerRect.y)
    }

    switchesRef.current.style.height = `${bottom - Math.max(0, switchesRect.y)}px`
    contentRef.current.style.height = `${bottom - Math.max(0, contentsRect.y)}px`

    switchesRef.current.scrollTop = sScrollTop
    contentRef.current.scrollTop = cScrollTop
  }

  useEffect(() => {
    resizeObserverRef.current?.disconnect()
    intersectionObserverRef.current?.disconnect()

    const throttledUpdate = () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current)
      throttleTimerRef.current = setTimeout(() => {
        window.requestAnimationFrame(() => updateSidebarHeight())
      }, 60)
    }

    // eslint-disable-next-line compat/compat
    resizeObserverRef.current = new window.ResizeObserver(throttledUpdate)
    // eslint-disable-next-line compat/compat
    resizeObserverRef.current.observe(document.scrollingElement)
    resizeObserverRef.current.observe(container)

    // eslint-disable-next-line compat/compat
    intersectionObserverRef.current = new window.IntersectionObserver(
      () => window.requestAnimationFrame(() => updateSidebarHeight()),
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    const header = document.querySelector('.-header')
    if (header != undefined) intersectionObserverRef.current.observe(header)
    const footer = document.querySelector('.-footer')
    if (footer != undefined) intersectionObserverRef.current.observe(footer)

    return () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current)

      resizeObserverRef.current?.disconnect()
      intersectionObserverRef.current?.disconnect()
    }
  }, [container])

  // The strip is clipped with no scrollbar of its own, so on a short window the lower switches are
  // reached with the mouse wheel, as on master: a wheel over the strip scrolls the strip (at half
  // the wheel's step) and never the page. A native listener, because React's wheel handler is
  // passive and cannot keep the page still.
  useEffect(() => {
    const switches = switchesRef.current
    if (!switches) return

    const onWheel = event => {
      event.preventDefault()
      event.stopPropagation()
      switches.scrollBy(0, event.deltaY / 2)
    }

    switches.addEventListener('wheel', onWheel, { passive: false })
    return () => switches.removeEventListener('wheel', onWheel, { passive: false })
  }, [])

  useEffect(
    () =>
      onLoad((key, props) =>
        dispatch({
          type: 'UPDATE_STATE',
          key,
          state: props
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEventListener('updateSidebarState', event => {
    const { key, props } = event.detail
    dispatch({
      type: 'UPDATE_STATE',
      key,
      state: Object.assign({}, state[key], props)
    })
  })

  // the panels are tinted and closely stacked, so a pale tooltip becomes one more pale shape among
  // them; the sidebar asks for the deep tone once rather than each mark in it
  return (
    <div className={style.component} data-tooltip-tone="deep">
      <div ref={switchesRef} className={style.switches} role="tablist" aria-label="Sidebar panels">
        <SidebarSwitches model={model} features={features} currentPanel={currentPanel} onPanelChange={onPanelChange} />
      </div>
      <div
        ref={contentRef}
        data-panel={currentPanel}
        className={style.content}
        role="tabpanel"
        aria-labelledby={`stat-sidebar-switch-${currentPanel}`}
      >
        <DataContext.Provider value={dataContextValue}>
          <SidebarContent
            container={container}
            sidebar={sidebar}
            survey={survey}
            model={model}
            parser={parser}
            features={features}
            currentPanel={currentPanel}
            onPanelChange={onPanelChange}
            {...props}
          />
        </DataContext.Provider>
      </div>
    </div>
  )
}

Sidebar.propTypes = {
  container: PropTypes.any,
  sidebar: PropTypes.any,
  survey: PropTypes.object,
  model: PropTypes.object,
  parser: PropTypes.any,
  features: PropTypes.object,
  currentPanel: PropTypes.string,
  onLoad: PropTypes.func,
  onPanelChange: PropTypes.func
}

Sidebar.displayName = 'Sidebar'

export default Sidebar

ADND().connect()
